import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatCard } from './ui/StatCard';
import { Spinner } from './ui/Spinner';
import { Modal } from './ui/Modal';
import { api } from '../api/client';
import { endpoints } from '../api/endpoints';
import { pathForMenu } from '../routes/menuPaths';
import { WORKFLOW_CHAIN, statusLabel } from './orders/workflowConstants';
import { ordersOpenOrderTarget, ordersPathWithStatus } from './orders/orderListFilterUtils';
import { OrderPeriodFilter } from './dashboard/OrderPeriodFilter';
import { buildOrdersAnalyticsParams, formatPeriodRange } from './dashboard/dashboardPeriodUtils';
import { usePermissions } from '../permissions/PermissionContext';

const formatCount = (n) => {
  if (n == null || Number.isNaN(Number(n))) {
    return '—';
  }
  return Number(n).toLocaleString();
};

const MASTER_METRICS = [
  {
    field: 'designCount',
    label: 'Designs',
    color: 'orange',
    menu: 'Designs',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    field: 'feederCount',
    label: 'Feeders',
    color: 'blue',
    menu: 'Feeders',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
  },
  {
    field: 'conceptCount',
    label: 'Concepts',
    color: 'green',
    menu: 'Concepts',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
  {
    field: 'subCategoryCount',
    label: 'Sub Categories',
    color: 'slate',
    menu: 'Sub Categories',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
      </svg>
    ),
  },
  {
    field: 'categoryCount',
    label: 'Categories',
    color: 'red',
    menu: 'Categories',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    ),
  },
  {
    field: 'colorCount',
    label: 'Colours',
    color: 'pink',
    menu: 'Colors',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
      </svg>
    ),
  },
];

const DEFECT_ICON = (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const ORDER_ICON = (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const TONE_TO_STAT_COLOR = {
  slate: 'slate',
  blue: 'blue',
  teal: 'teal',
  indigo: 'indigo',
  violet: 'purple',
  pink: 'pink',
  emerald: 'green',
  sky: 'blue',
  rose: 'red',
};

const ORDER_PIPELINE_METRICS = [
  {
    field: 'TOTAL',
    label: 'All orders',
    color: 'slate',
    statusKey: '',
    menu: 'Orders',
    icon: ORDER_ICON,
  },
  ...WORKFLOW_CHAIN.map((w) => ({
    field: w.key,
    label: w.label,
    color: TONE_TO_STAT_COLOR[w.tone] || 'slate',
    statusKey: w.key,
    menu: w.key === 'IN_STOCK' ? 'Stock' : 'Orders',
    icon: ORDER_ICON,
  })),
];

function formatMachineProgress(produced, planned) {
  const p = Number(produced) || 0;
  const t = Number(planned) || 0;
  if (t <= 0) return `${p} pcs`;
  return `${p}/${t} pcs`;
}

function formatOrderCode(code) {
  const c = String(code || '').trim();
  return c || '—';
}

function filterMachines(machines, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return machines;
  return machines.filter((m) => {
    const hay = [
      m.machineNo,
      m.machineLabel,
      m.orderCode,
      m.designSampleNo,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
}

function ActiveMachinesTable({ machines, onOpenOrder, canOpenOrder }) {
  if (!machines.length) {
    return <p className="text-sm text-slate-500 py-6 text-center">No active machines.</p>;
  }

  const cell = 'py-2.5 px-3';
  const headCell = `${cell} sticky top-0 z-20 bg-slate-50 border-b border-slate-200 shadow-[0_1px_0_0_rgba(226,232,240,1)]`;

  return (
    <table className="min-w-full text-sm border-collapse">
      <thead>
        <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          <th className={`${headCell} pl-4`}>Machine</th>
          <th className={headCell}>Status</th>
          <th className={headCell}>Order</th>
          <th className={headCell}>Design</th>
          <th className={`${headCell} pr-4`}>Progress</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 bg-white">
        {machines.map((m, idx) => {
          const isRunning = m.beamStatus === 'RUNNING';
          return (
            <tr key={`${m.machineNo}-${m.orderId}-${idx}`} className="text-slate-800">
              <td className={`${cell} pl-4 font-medium whitespace-nowrap`}>
                {m.machineNo}
                {m.machineLabel ? (
                  <span className="block text-xs font-normal text-slate-500">{m.machineLabel}</span>
                ) : null}
              </td>
              <td className={`${cell} whitespace-nowrap`}>
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    isRunning ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'
                  }`}
                >
                  {isRunning ? 'Running' : 'Hold'}
                </span>
              </td>
              <td className={`${cell} whitespace-nowrap`}>
                {m.orderId && canOpenOrder ? (
                  <button
                    type="button"
                    onClick={() => onOpenOrder(m.orderId)}
                    className="font-medium text-primary-700 hover:underline"
                  >
                    {formatOrderCode(m.orderCode)}
                  </button>
                ) : (
                  formatOrderCode(m.orderCode)
                )}
              </td>
              <td className={`${cell} text-slate-600 max-w-[180px] truncate`}>
                {m.designSampleNo || '—'}
              </td>
              <td className={`${cell} pr-4 whitespace-nowrap tabular-nums`}>
                {formatMachineProgress(m.producedPieces, m.plannedPieces)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ProductionMachinesModal({ unit, open, onClose, onOpenOrder, canOpenOrder }) {
  const [search, setSearch] = useState('');
  const machines = useMemo(
    () => (Array.isArray(unit?.activeMachines) ? unit.activeMachines : []),
    [unit]
  );
  const filtered = useMemo(() => filterMachines(machines, search), [machines, search]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  if (!unit) return null;

  const running = Number(unit.runningMachines) || 0;
  const registered = Number(unit.registeredMachines) || 0;
  const onHold = Number(unit.machinesOnHold) || 0;
  const listLabel = search && filtered.length !== machines.length
    ? `${formatCount(filtered.length)} of ${formatCount(machines.length)} machines`
    : `${formatCount(machines.length)} machine${machines.length === 1 ? '' : 's'}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={unit.khataName}
      size="2xl"
      bodyClassName="!px-4 !py-3 flex flex-col min-h-0 !overflow-hidden"
    >
      <div className="shrink-0 space-y-3">
        <p className="text-sm text-slate-600 tabular-nums">
          {formatCount(running)} running
          {onHold ? ` · ${formatCount(onHold)} on hold` : ''}
          {registered ? ` · ${formatCount(registered)} registered` : ''}
        </p>
        {machines.length > 0 ? (
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search machine, order, design…"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30"
          />
        ) : null}
      </div>

      <div
        className="mt-3 flex-1 min-h-[12rem] max-h-[min(70vh,640px)] overflow-auto overscroll-contain rounded-lg border border-slate-200 bg-slate-50/50 touch-pan-y [-webkit-overflow-scrolling:touch]"
        role="region"
        aria-label="Active machines list"
        tabIndex={0}
      >
        <ActiveMachinesTable machines={filtered} onOpenOrder={onOpenOrder} canOpenOrder={canOpenOrder} />
      </div>

      <p className="shrink-0 text-xs text-slate-500 mt-2 pt-1 border-t border-slate-100 tabular-nums">
        {listLabel}
      </p>
    </Modal>
  );
}

function ProductionUnitCard({ unit, onOpen, restricted, restrictReason }) {
  const running = Number(unit.runningMachines) || 0;
  const registered = Number(unit.registeredMachines) || 0;
  const onHold = Number(unit.machinesOnHold) || 0;
  const body = (
    <>
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold text-slate-900 truncate">{unit.khataName}</h3>
        <span className="text-xl font-bold tabular-nums text-slate-900 shrink-0">
          {registered ? `${running}/${registered}` : formatCount(running)}
        </span>
      </div>
      {onHold ? (
        <p className="text-xs text-amber-700 mt-1">{formatCount(onHold)} on hold</p>
      ) : null}
      {restricted ? (
        <p className="text-[10px] font-medium text-slate-400 mt-1">View only</p>
      ) : null}
    </>
  );

  if (restricted) {
    return (
      <div
        title={restrictReason}
        className="w-full text-left rounded-xl border border-slate-200/80 bg-white shadow-soft p-4 ring-1 ring-slate-100"
      >
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left rounded-xl border border-slate-200/80 bg-white shadow-soft p-4 hover:shadow-medium hover:border-primary-200 transition-all"
    >
      {body}
    </button>
  );
}

function DamagedPiecesModal({ open, onClose, rows, onOpenOrder, canOpenOrder }) {
  const byDesign = useMemo(() => {
    const map = new Map();
    for (const row of rows || []) {
      const key = row.designSampleNo?.trim() || '(No design)';
      map.set(key, (map.get(key) || 0) + (Number(row.damagedPieces) || 0));
    }
    return [...map.entries()]
      .map(([design, total]) => ({ design, total }))
      .sort((a, b) => b.total - a.total);
  }, [rows]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Damaged pieces"
      size="xl"
    >
      {byDesign.length > 0 ? (
        <div className="mb-4 rounded-lg border border-amber-100 bg-amber-50/60 px-3 py-2">
          <p className="text-xs font-semibold text-amber-900 mb-1">By design</p>
          <ul className="space-y-1 text-sm text-amber-950">
            {byDesign.slice(0, 8).map(({ design, total }) => (
              <li key={design} className="flex justify-between gap-4">
                <span className="truncate font-medium">{design}</span>
                <span className="tabular-nums shrink-0">{formatCount(total)} pcs</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {!rows?.length ? (
        <p className="text-sm text-slate-600">No damaged pieces recorded yet.</p>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="pb-2 px-2">Order</th>
                <th className="pb-2 px-2">Design</th>
                <th className="pb-2 px-2 hidden sm:table-cell">Party</th>
                <th className="pb-2 px-2">Damaged</th>
                <th className="pb-2 px-2 hidden md:table-cell">Order total</th>
                <th className="pb-2 px-2 hidden lg:table-cell">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => {
                const damaged = Number(row.damagedPieces) || 0;
                const total = Number(row.totalPieces) || 0;
                const pct = total > 0 ? Math.round((damaged / total) * 100) : null;
                return (
                  <tr key={row.orderId} className="text-slate-800">
                    <td className="py-2.5 px-2 whitespace-nowrap">
                      {canOpenOrder ? (
                        <button
                          type="button"
                          onClick={() => onOpenOrder(row.orderId)}
                          className="font-medium text-primary-700 hover:underline"
                        >
                          {formatOrderCode(row.orderCode)}
                        </button>
                      ) : (
                        <span className="font-medium text-slate-800">{formatOrderCode(row.orderCode)}</span>
                      )}
                    </td>
                    <td className="py-2.5 px-2 text-slate-600 max-w-[140px] truncate">
                      {row.designSampleNo || '—'}
                    </td>
                    <td className="py-2.5 px-2 hidden sm:table-cell text-slate-600 max-w-[120px] truncate">
                      {row.partyName || '—'}
                    </td>
                    <td className="py-2.5 px-2 whitespace-nowrap">
                      <span className="font-semibold text-rose-700 tabular-nums">
                        {formatCount(damaged)}
                      </span>
                      {pct != null ? (
                        <span className="text-xs text-slate-500 ml-1">({pct}%)</span>
                      ) : null}
                    </td>
                    <td className="py-2.5 px-2 hidden md:table-cell tabular-nums text-slate-600">
                      {total > 0 ? formatCount(total) : '—'}
                    </td>
                    <td className="py-2.5 px-2 hidden lg:table-cell text-slate-500 text-xs">
                      {statusLabel(row.workflowStatus)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function DashboardHome() {
  const navigate = useNavigate();
  const { canViewMenu } = usePermissions();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [machinesModalUnit, setMachinesModalUnit] = useState(null);
  const [damagedModalOpen, setDamagedModalOpen] = useState(false);
  const [pipelinePeriod, setPipelinePeriod] = useState('ALL');
  const [pipelineCustomFrom, setPipelineCustomFrom] = useState('');
  const [pipelineCustomTo, setPipelineCustomTo] = useState('');
  const [pipelineAnalytics, setPipelineAnalytics] = useState(null);
  const [pipelineAnalyticsLoading, setPipelineAnalyticsLoading] = useState(false);
  const [pipelineAnalyticsError, setPipelineAnalyticsError] = useState('');

  const loadDashboardSummary = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const { data } = await api.get(endpoints.dashboard.summary);
      setSummary(data);
    } catch (e) {
      setSummaryError(e?.message || 'Failed to load dashboard');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardSummary();
  }, [loadDashboardSummary]);

  const loadPipelineAnalytics = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (pipelinePeriod === 'CUSTOM' && (!pipelineCustomFrom || !pipelineCustomTo)) {
      return;
    }
    setPipelineAnalyticsLoading(true);
    setPipelineAnalyticsError('');
    try {
      const qs = buildOrdersAnalyticsParams({
        period: pipelinePeriod,
        from: pipelineCustomFrom,
        to: pipelineCustomTo,
      });
      const { data } = await api.get(`${endpoints.dashboard.ordersAnalytics}?${qs}`);
      setPipelineAnalytics(data);
    } catch (e) {
      setPipelineAnalyticsError(e?.message || 'Failed to load order pipeline');
      setPipelineAnalytics(null);
    } finally {
      setPipelineAnalyticsLoading(false);
    }
  }, [pipelinePeriod, pipelineCustomFrom, pipelineCustomTo]);

  useEffect(() => {
    loadPipelineAnalytics();
  }, [loadPipelineAnalytics]);

  const goToMenu = (menu) => {
    if (!menu) return;
    navigate(pathForMenu(menu));
    try {
      localStorage.setItem('activeMenu', menu);
    } catch {
      // ignore
    }
  };

  const goToOrder = useCallback(
    (orderId) => {
      if (!orderId) return;
      navigate(ordersOpenOrderTarget(orderId));
      try {
        localStorage.setItem('activeMenu', 'Orders');
      } catch {
        // ignore
      }
    },
    [navigate]
  );

  const goToOrderMetric = (metric) => {
    if (metric.menu === 'Stock') {
      goToMenu('Stock');
      return;
    }
    const path = metric.statusKey ? ordersPathWithStatus(metric.statusKey) : pathForMenu('Orders');
    navigate(path);
    try {
      localStorage.setItem('activeMenu', 'Orders');
    } catch {
      // ignore
    }
  };

  const orderCountFor = (field) => {
    const byStatus = pipelineAnalytics?.orderCountsByStatus;
    if (byStatus && byStatus[field] != null) {
      return byStatus[field];
    }
    if (field === 'TOTAL' && pipelineAnalytics) {
      return pipelineAnalytics.orderTotalCount;
    }
    return 0;
  };

  const pipelineRangeLabel = formatPeriodRange(pipelineAnalytics);

  const productionUnits = Array.isArray(summary?.productionUnits) ? summary.productionUnits : [];
  const damagedOrders = Array.isArray(summary?.damagedOrders) ? summary.damagedOrders : [];
  const totalDamaged = summary?.totalDamagedPieces ?? 0;

  const refreshDashboard = () => {
    loadDashboardSummary();
    loadPipelineAnalytics();
  };

  const canOpenMenu = useCallback(
    (menuId) => Boolean(menuId && canViewMenu(menuId)),
    [canViewMenu]
  );

  const restrictLabel = useCallback(
    (menuId) => {
      const label = String(menuId || 'this section');
      return `View only — you don't have access to ${label}`;
    },
    []
  );

  const canOpenOrders = canOpenMenu('Orders');
  const canOpenProductionUnits = canOpenMenu('Production Units');

  return (
    <div className="space-y-6 animate-fade-in-up">
      {summaryError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50/90 text-rose-800 px-4 py-3 text-sm">
          {summaryError}
        </div>
      ) : null}

      <section>
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-base font-semibold text-slate-900">Masters</h2>
          <button
            type="button"
            onClick={refreshDashboard}
            disabled={summaryLoading || pipelineAnalyticsLoading}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
            aria-label="Refresh dashboard"
          >
            {summaryLoading || pipelineAnalyticsLoading ? (
              <Spinner size="sm" />
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            Refresh
          </button>
        </div>
        <div className="grid gap-3 sm:gap-4 grid-cols-2 md:grid-cols-3 [@media(min-width:1100px)]:grid-cols-[repeat(auto-fit,minmax(170px,1fr))]">
          {MASTER_METRICS.map((m) => {
            const clickable = canOpenMenu(m.menu);
            return (
            <StatCard
              key={m.field}
              title={m.label}
              color={m.color}
              icon={m.icon}
              loading={summaryLoading && summary == null}
              value={summary ? formatCount(summary[m.field]) : (summaryError ? '—' : '…')}
              onClick={clickable ? () => goToMenu(m.menu) : undefined}
              restricted={!clickable}
              restrictReason={!clickable ? restrictLabel(m.menu) : undefined}
            />
            );
          })}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Order pipeline</h2>
            {pipelineRangeLabel ? (
              <p className="text-xs text-slate-500 mt-0.5">{pipelineRangeLabel}</p>
            ) : (
              <p className="text-xs text-slate-500 mt-0.5">Counts by order stage for the selected period</p>
            )}
          </div>
          <OrderPeriodFilter
            period={pipelinePeriod}
            onPeriodChange={setPipelinePeriod}
            customFrom={pipelineCustomFrom}
            customTo={pipelineCustomTo}
            onCustomFromChange={setPipelineCustomFrom}
            onCustomToChange={setPipelineCustomTo}
          />
        </div>

        {pipelineAnalyticsError ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50/90 text-rose-800 px-4 py-3 text-sm">
            {pipelineAnalyticsError}
          </div>
        ) : null}

        <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
          {ORDER_PIPELINE_METRICS.map((m) => {
            const clickable = canOpenMenu(m.menu);
            return (
            <StatCard
              key={m.field}
              title={m.label}
              color={m.color}
              icon={m.icon}
              loading={pipelineAnalyticsLoading && pipelineAnalytics == null}
              value={
                pipelineAnalytics
                  ? formatCount(orderCountFor(m.field))
                  : pipelineAnalyticsError
                    ? '—'
                    : '…'
              }
              onClick={clickable ? () => goToOrderMetric(m) : undefined}
              restricted={!clickable}
              restrictReason={!clickable ? restrictLabel(m.menu) : undefined}
            />
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="text-base font-semibold text-slate-900 mb-3">Production floor</h2>
        {summaryLoading && summary == null ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-28 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : productionUnits.length === 0 ? (
          <p className="text-sm text-slate-500">No production units.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {productionUnits.map((unit) => (
              <ProductionUnitCard
                key={unit.khataId || unit.khataName}
                unit={unit}
                onOpen={() => setMachinesModalUnit(unit)}
                restricted={!canOpenProductionUnits}
                restrictReason={!canOpenProductionUnits ? restrictLabel('Production Units') : undefined}
              />
            ))}
          </div>
        )}
        <ProductionMachinesModal
          unit={machinesModalUnit}
          open={Boolean(machinesModalUnit)}
          onClose={() => setMachinesModalUnit(null)}
          canOpenOrder={canOpenOrders}
          onOpenOrder={(id) => {
            setMachinesModalUnit(null);
            goToOrder(id);
          }}
        />
      </section>

      <section>
        <h2 className="text-base font-semibold text-slate-900 mb-3">Damaged pieces</h2>
        <div className="max-w-xs">
          <StatCard
            title="Total damaged"
            color="red"
            icon={DEFECT_ICON}
            loading={summaryLoading && summary == null}
            value={summary ? formatCount(totalDamaged) : (summaryError ? '—' : '…')}
            onClick={canOpenOrders ? () => setDamagedModalOpen(true) : undefined}
            restricted={!canOpenOrders}
            restrictReason={!canOpenOrders ? restrictLabel('Orders') : undefined}
          />
        </div>
        <DamagedPiecesModal
          open={damagedModalOpen}
          onClose={() => setDamagedModalOpen(false)}
          rows={damagedOrders}
          canOpenOrder={canOpenOrders}
          onOpenOrder={(id) => {
            setDamagedModalOpen(false);
            goToOrder(id);
          }}
        />
      </section>
    </div>
  );
}

export default DashboardHome;
